<script setup lang="ts">
import { explainMonsterAffix, explainMonsterAttrsWeight } from '@/core/monster/affix'
import { useUserStore } from '@/stores/modules/user'
import type { NestMonsterType } from '@/types/monster'
import { socketEmitAsync } from '@/utils/web-socket'
import { QuestionFilled } from '@element-plus/icons-vue'
import { useRoomStore } from '@/stores/modules/room'
import { useCommonStore } from '@/stores/modules/common'

const commonStore = useCommonStore()
const userStore = useUserStore()
const roomStore = useRoomStore()

defineProps<{ nestMonster?: NestMonsterType }>()
const emits = defineEmits(['getMonster', 'start', 'giveUp', 'sweep', 'showRoom'])

function handleHuntNestMonster() {
  if (userStore.nestTimes <= 0) return ElMessage.warning('无奖励时无法进行探寻')
  if (userStore.checkPetCapacity) return ElMessage.warning('魔物牧场已满，无法进行探寻')

  ElMessageBox.confirm('<p class="affix">确定<span class="info">探寻</span>【<span class="pet">魔物巢穴</span>】吗？</p>', '提示', {
    type: 'warning',
    appendTo: 'body',
    dangerouslyUseHTMLString: true,
    confirmButtonClass: 'is-plain',
  }).then(async () => {
    const res = await socketEmitAsync<NestMonsterType>({ type: 'huntNestMonster' })
    emits('getMonster', res)
    ElMessage.success('探寻成功')
  })
}
async function handleStartNest() {
  if (userStore.checkPetCapacity) return ElMessage.warning('魔物牧场已满，无法进行战斗')
  if (roomStore.id) return start()
  ElMessageBox.confirm(
    '<p class="affix">确定<span class="hp">独自</span>进行【<span class="pet">魔物巢穴</span>】<span class="info">战斗</span>吗？</p>',
    '提示',
    {
      type: 'warning',
      appendTo: 'body',
      dangerouslyUseHTMLString: true,
      confirmButtonClass: 'is-plain',
    },
  ).then(() => start())

  async function start() {
    const res = await socketEmitAsync<NestMonsterType>({ type: 'startNestFight' })
    roomStore.clearRoomInfo()
    emits('start', { ...res, type: 'nest' })
  }
}
function handleRefreshNest() {
  if (userStore.gold < 50000) return ElMessage.warning('重新探寻需要 50,000金币')
  if (userStore.checkPetCapacity) return ElMessage.warning('魔物牧场已满，无法进行探寻')
  if (roomStore.id) return ElMessage.warning('无法在房间内时重新探寻魔物')
  ElMessageBox.confirm(
    '<p class="affix">确定<span class="hp">放弃</span>捕捉当前魔物并进行重新<span class="info">探寻</span>吗？（消耗 <span class="gold">50,000</span>金币）</p>',
    '提示',
    {
      type: 'warning',
      appendTo: 'body',
      dangerouslyUseHTMLString: true,
      confirmButtonClass: 'is-plain',
    },
  ).then(async () => {
    const res = await socketEmitAsync<NestMonsterType>({ type: 'refreshNestMonster' })
    userStore.gold -= 50000
    emits('getMonster', res)
    ElMessage.success('重新探寻成功')
  })
}

function handleOpenRoom() {
  if (userStore.checkPetCapacity) return ElMessage.warning('魔物牧场已满，无法进行战斗')
  emits('showRoom')
}

function handleSweep() {
  if (userStore.nestTimes <= 0) return ElMessage.warning('您已无魔物巢穴奖励次数')
  ElMessageBox.confirm('<p class="affix">确定<span class="info">扫荡</span>魔物巢穴吗？（获得剩余奖励次数的<b>随机</b>魔物）</p>', '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      await socketEmitAsync({ type: 'sweepNest' })
      userStore.nestTimes = 0
      ElMessage.success('扫荡成功，请在邮件处领取奖励')
      useCommonStore().getMailList()
      emits('sweep')
    })
    .catch(() => {})
}
</script>

<template>
  <div class="border-wrap nest">
    <h4 class="pet">
      魔物巢穴
      <div class="help-info" style="margin-left: 5px">
        <el-popover placement="top" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix" style="max-height: 60vh; overflow: auto">
            <h4>魔物巢穴说明</h4>
            <p>1. 魔物巢穴<span class="info">每周日0点刷新</span>，刷新后将重置奖励次数。</p>
            <p>
              2. 通过<span class="info">探寻</span>，您会随机遭遇一只<span class="pet">魔物</span>，魔物属性随机，同时每种的魔物都有不同的<span
                class="info"
                >属性倾向</span
              >（影响魔物产出时加成的战斗属性）。
            </p>
            <p>3. 参与魔物巢穴战斗您将必定获得对应击败的<span class="pet">魔物</span>，用于在<span class="pet">魔物牧场</span>当中提供各项增益。</p>
            <p>
              4. 魔物巢穴支持<span class="info">双人组队</span>，当组队挑战时，怪物的血量增加<span class="grow">0.8</span
              >倍，同时还会略微增加魔物的品质爆率，组队挑战时无法中途取消挑战。
            </p>
            <p>5. 战斗时支持<span class="info">切换装备即刻生效</span>，当输出不足时，请及时更换装备。</p>
            <p>6. 战斗失败时，<span class="grow">不会消耗</span>奖励次数；当奖励次数用尽时，您无法进行探寻及获得奖励，但您仍可以帮助其他玩家战斗。</p>
            <p>7. 阶级越高的魔物挑战难度越大，若难以攻克可尝试<span class="grow">组队挑战</span>，或重新探寻低阶魔物。</p>
            <p>
              8. 品质爆率：<span class="normal">普通</span> 77%，<span class="race">稀有</span> 20%，<span class="epic">史诗</span>
              3%（组队战斗时增加1%的<span class="epic">史诗</span>爆率）
            </p>
          </div>
        </el-popover>
      </div>
    </h4>
    <p>剩余次数：{{ userStore.nestTimes }}</p>
    <p>
      当前怪物：
      <el-popover placement="bottom" :width="300" trigger="click" popper-class="monster-detail" :teleported="false" :disabled="!nestMonster?.name">
        <template #reference>
          <el-button size="small" type="danger" plain>
            <span v-show="nestMonster?.name">{{ nestMonster?.name }}</span>
            <span v-show="!nestMonster?.name">（暂未探寻）</span>
          </el-button>
        </template>
        <div class="monster-detail" v-if="nestMonster">
          <h3 style="margin: 5px 0">👾{{ nestMonster.name }}👾</h3>
          <p>魔物阶级：{{ nestMonster?.rank }}</p>
          <p>属性倾向：{{ explainMonsterAttrsWeight(nestMonster.attrsWeight) }}</p>
          <p>血量：{{ nestMonster.hp }}</p>
          <p>防御：{{ nestMonster.def }}</p>
          <p>闪避率：{{ nestMonster.evs }}%</p>
          <p>抗爆率：{{ nestMonster.crtdef }}%</p>
          <template v-if="nestMonster.special?.length">
            <p>特性：</p>
            <p v-for="item of explainMonsterAffix(nestMonster.special)" v-html="item"></p>
          </template>
        </div>
      </el-popover>
    </p>
    <div style="margin: 10px 0">
      <div v-show="!nestMonster?.name">
        <el-button type="warning" plain @click="handleHuntNestMonster" :disabled="userStore.dailyChallengeLevel.diamond < 3">
          {{ userStore.dailyChallengeLevel.diamond < 3 ? '通过钻石挑战 3级解锁' : '探寻' }}
        </el-button>
        <el-button type="primary" plain :disabled="userStore.dailyChallengeLevel.diamond < 3" @click="handleOpenRoom"> 组队战斗 </el-button>
        <el-button color="#f7c99c" :dark="commonStore.isDarkMode" plain @click="handleSweep" v-show="userStore.advanceLevel === 4">扫荡</el-button>
      </div>
      <div v-show="nestMonster?.name">
        <el-button type="success" plain @click="() => handleStartNest()" :disabled="Boolean(roomStore.id)">独自战斗</el-button>
        <el-button type="primary" plain @click="handleOpenRoom">组队战斗</el-button>
        <el-button type="danger" plain @click="handleRefreshNest">重新探寻</el-button>
        <el-button color="#f7c99c" :dark="commonStore.isDarkMode" plain @click="handleSweep" v-show="userStore.advanceLevel === 4">扫荡</el-button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped></style>
